import React from 'react';
import type {
  EvaluationItem,
  EvaluationItemType,
  FeedbackItem,
  FeedbackItemType,
} from '../../../../types/course';
import { uid } from './shared/tokens';

import MultipleChoiceItem from './items/MultipleChoiceItem';
import BooleanTrueFalseItem from './items/BooleanTrueFalseItem';
import TupleMatchingItem from './items/TupleMatchingItem';
import CompletionFillBlankItem from './items/CompletionFillBlankItem';
import FreeTextEssayItem from './items/FreeTextEssayItem';
import ShortAnswerTextItem from './items/ShortAnswerTextItem';
import OrderingSequenceItem from './items/OrderingSequenceItem';
import NumericInputItem from './items/NumericInputItem';
import CodeSubmissionItem from './items/CodeSubmissionItem';

import FreeInputFeedbackItem from './feedback-items/FreeInputFeedbackItem';
import LikertScaleItem from './feedback-items/LikertScaleItem';

import MultipleChoiceIcon from '../../../icons/modules/quizTypes/MultipleChoiceIcon';
import BooleanIcon from '../../../icons/modules/quizTypes/BooleanIcon';
import MatchingIcon from '../../../icons/modules/quizTypes/MatchingIcon';
import CompletionIcon from '../../../icons/modules/quizTypes/CompletionIcon';
import EssayIcon from '../../../icons/modules/quizTypes/EssayIcon';

// ─── Inline icons for item types with no dedicated asset ────────────────

type IconProps = { className?: string };

const ShortAnswerIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" width="21" height="20">
    <path d="M3 7h18M3 12h14M3 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const OrderingIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" width="21" height="20">
    <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 4l-4 2 4 2M16 10l4 2-4 2M8 16l-4 2 4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NumericIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" width="21" height="20">
    <path d="M9 4L7 20M17 4l-2 16M4 9h16M3 15h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CodeIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" width="21" height="20">
    <path d="M8 6l-5 6 5 6M16 6l5 6-5 6M14 4l-4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ScalingIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" width="21" height="20">
    <path d="M12 3l2.5 6 6.5.5-5 4.5 1.5 6.5L12 17l-5.5 3.5L8 14l-5-4.5 6.5-.5L12 3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const FreeFeedbackIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" width="21" height="20">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

// ─── Evaluation registry ────────────────────────────────────────────────

type EvaluationDispatchEntry = {
  type: EvaluationItemType;
  label: string;
  description: string;
  Icon: React.FC<IconProps>;
  Component: React.FC<{ item: any; onChange: (patch: any) => void; onDelete: () => void; readOnly?: boolean; onTypeToggle?: () => void }>;
  defaultItem: (order_index: number) => EvaluationItem;
};

export const EVALUATION_REGISTRY: EvaluationDispatchEntry[] = [
  {
    type: 'multiple_choice',
    label: 'Single / Multiple Choice',
    description: 'Choose one or more correct answers',
    Icon: MultipleChoiceIcon,
    Component: MultipleChoiceItem,
    defaultItem: (order_index) => {
      const opt1 = uid('opt');
      const opt2 = uid('opt');
      return {
        id: uid('item'),
        type: 'multiple_choice',
        order_index,
        points: 1,
        config: {
          question: 'New question',
          options: [
            { id: opt1, text: 'Option 1' },
            { id: opt2, text: 'Option 2' },
          ],
          correct_answers: [opt1],
        },
      };
    },
  },
  {
    type: 'boolean_true_false',
    label: 'True / False',
    description: 'Simple true or false question',
    Icon: BooleanIcon,
    Component: BooleanTrueFalseItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'boolean_true_false',
      order_index,
      points: 1,
      config: { question: 'New statement', correct_value: true },
    }),
  },
  {
    type: 'tuple_matching',
    label: 'Matching',
    description: 'Match items from two columns',
    Icon: MatchingIcon,
    Component: TupleMatchingItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'tuple_matching',
      order_index,
      points: 1,
      config: {
        question: 'New question',
        pairs: [
          { id: uid('pair'), left: 'Left 1', right: 'Right 1' },
          { id: uid('pair'), left: 'Left 2', right: 'Right 2' },
        ],
      },
    }),
  },
  {
    type: 'completion_fill_blank',
    label: 'Completion',
    description: 'Fill in the blank(s)',
    Icon: CompletionIcon,
    Component: CompletionFillBlankItem,
    defaultItem: (order_index) => {
      const blankId = uid('blank');
      return {
        id: uid('item'),
        type: 'completion_fill_blank',
        order_index,
        points: 1,
        config: {
          prompt: 'The [[Pacific]] is the largest ocean on Earth.',
          blanks: [{ id: blankId, answer: 'Pacific' }],
        },
      };
    },
  },
  {
    type: 'free_text_essay',
    label: 'Essay',
    description: 'Long-form written response',
    Icon: EssayIcon,
    Component: FreeTextEssayItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'free_text_essay',
      order_index,
      points: 5,
      config: { prompt: 'New essay question' },
    }),
  },
  {
    type: 'short_answer_text',
    label: 'Short Answer',
    description: 'A brief text answer that matches exactly',
    Icon: ShortAnswerIcon,
    Component: ShortAnswerTextItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'short_answer_text',
      order_index,
      points: 1,
      config: { question: 'New question', expected_answer: 'answer' },
    }),
  },
  {
    type: 'ordering_sequence',
    label: 'Ordering',
    description: 'Arrange items in the right order',
    Icon: OrderingIcon,
    Component: OrderingSequenceItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'ordering_sequence',
      order_index,
      points: 1,
      config: {
        question: 'New question',
        items: [
          { id: uid('ord'), text: 'Item 1' },
          { id: uid('ord'), text: 'Item 2' },
        ],
      },
    }),
  },
  {
    type: 'numeric_input',
    label: 'Numeric',
    description: 'Answer is a number with optional tolerance',
    Icon: NumericIcon,
    Component: NumericInputItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'numeric_input',
      order_index,
      points: 1,
      config: { question: 'New question', correct_value: 0 },
    }),
  },
  {
    type: 'code_submission',
    label: 'Code',
    description: 'Write a code snippet',
    Icon: CodeIcon,
    Component: CodeSubmissionItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'code_submission',
      order_index,
      points: 5,
      config: { prompt: 'New coding task', language: 'python' },
    }),
  },
];

export function getEvaluationEntry(type: EvaluationItemType): EvaluationDispatchEntry {
  const entry = EVALUATION_REGISTRY.find((e) => e.type === type);
  if (!entry) throw new Error(`Unknown evaluation item type: ${type}`);
  return entry;
}

// ─── Feedback registry ──────────────────────────────────────────────────

type FeedbackDispatchEntry = {
  type: FeedbackItemType;
  label: string;
  description: string;
  Icon: React.FC<IconProps>;
  Component: React.FC<{ item: any; onChange: (patch: any) => void; onDelete: () => void; readOnly?: boolean }>;
  defaultItem: (order_index: number) => FeedbackItem;
};

export const FEEDBACK_REGISTRY: FeedbackDispatchEntry[] = [
  {
    type: 'likert_5_scale',
    label: 'Scaling answer',
    description: 'Rate on a 3 / 5 / 10-point scale',
    Icon: ScalingIcon,
    Component: LikertScaleItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'likert_5_scale',
      order_index,
      required: true,
      config: { question: 'New question' },
    }),
  },
  {
    type: 'free_input_feedback',
    label: 'Free text answer',
    description: 'Open-ended written feedback',
    Icon: FreeFeedbackIcon,
    Component: FreeInputFeedbackItem,
    defaultItem: (order_index) => ({
      id: uid('item'),
      type: 'free_input_feedback',
      order_index,
      required: false,
      config: { question: 'New question' },
    }),
  },
];

export function getFeedbackEntry(type: FeedbackItemType): FeedbackDispatchEntry {
  // Likert 3 / 5 / 10 all dispatch to the same editor entry.
  if (type === 'likert_3_scale' || type === 'likert_10_scale' || type === 'likert_5_scale') {
    return FEEDBACK_REGISTRY[0];
  }
  return FEEDBACK_REGISTRY[1];
}
